import { type FC } from 'react'
import DatePicker from 'react-datepicker'
import { Button } from '../../../../../../components/Button/Button'
import { type IntegrationWorkflowTemplate } from '../../../../../../models/IntegrationWorkflow'
import { type IntegrationWorkflowTarget } from './integrationWorkflow'
import { useStartIntegrationWorkflow } from './useIntegrationWorkflowActions'

export interface StartWorkflowFormProps {
  target: IntegrationWorkflowTarget
  template: IntegrationWorkflowTemplate
  suggestedLabel?: string | null
  suggestedSummary?: string | null
  onStarted?: () => void
}

const DATE_FORMATTER = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' })

interface DateFieldProps {
  label: string
  value: Date | null
  placeholder: string
  onChange: (date: Date | null) => void
}

export const DateField: FC<DateFieldProps> = ({ label, value, placeholder, onChange }) => (
  <DatePicker
    selected={value}
    onChange={onChange}
    showTimeSelect
    todayButton='Today'
    customInput={
      <Button variant='dashedButton' className='button-small' aria-label={label}>
        <i className='bi bi-calendar-week' />
        {value ? DATE_FORMATTER.format(value) : placeholder}
      </Button>
    }
  />
)

interface StartWorkflowActionsProps {
  target: IntegrationWorkflowTarget
  templateId: string
  buildPayload: (() => Record<string, unknown>) | null
  onStarted?: () => void
}

export const StartWorkflowActions: FC<StartWorkflowActionsProps> = ({ target, templateId, buildPayload, onStarted }) => {
  const [start, { isLoading, isError }] = useStartIntegrationWorkflow(target)

  const startWorkflow = async (build: () => Record<string, unknown>) => {
    const result = await start({ templateId, payload: build() })
    if (result.data) {
      onStarted?.()
    }
  }

  return (
    <div className='d-flex align-items-center justify-content-end gap-3'>
      {isError ? <span className='paragraph-extra-small zocks-text-danger'>We could not start this workflow. Please try again.</span> : null}
      <Button
        variant='secondary'
        className='button-small'
        disabled={!buildPayload}
        loading={isLoading}
        onClick={() => (buildPayload ? startWorkflow(buildPayload) : null)}
      >
        <i className='bi bi-play-btn' />
        Start Workflow
      </Button>
    </div>
  )
}
